import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { Button, Drawer, Flex, Steps } from 'antd';
import collect from 'collect.js';
import { useSelector } from 'react-redux';
import useForm from '../../../hooks/utility/useForm';
import LocationHelpers from '../../../services/helpers/LocationHelpers';
import CompanyLocation from './CompanyLocation';
import CuisineType from './CuisineType';
import RestaurantType from './RestaurantType';
import CompanyProviders from './CompanyProviders';
import BusinessDetails from './BusinessDetails';

export const Step = {
  BasicInfo: 'basic_info',
  BusinessDetails: 'business_details',
};

export default function AddCompanyModal({ company, isDemo, onSave, onClose }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:AddCompanyModal' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const [activeStep, setActiveStep] = useState(Step.BasicInfo);
  const [loading, setLoading] = useState(false);
  const _steps = useMemo(
    () =>
      collect([{ key: Step.BasicInfo }])
        .when(!isDemo && serviceProvider?.is_master, (collection) => collection.push({ key: Step.BusinessDetails }))
        .toArray(),
    [isDemo, serviceProvider?.is_master],
  );
  const activeStepIndex = useMemo(
    () => collect(_steps).search((item) => item.key === activeStep),
    [activeStep, _steps],
  );

  const { data, update } = useForm({
    google_place: company?.google_place ?? null,
    name: company?.name ?? null,
    address: company?.address ?? null,
    city: company?.city ?? null,
    state: company?.state ?? null,
    country: company?.country ?? serviceProvider?.country,
    zip_code: company?.zip_code ?? null,
    lat: company?.lat,
    lon: company?.lon,
    // Inferred from the address on the location step and confirmed there
    // (PROD-489). Without it the backend falls back to the merchant's legal
    // region, which is one row per country.
    timezone: company?.timezone ?? null,

    restaurant_type: company?.restaurant_type ?? null,
    cuisine_types: company?.cuisine_types ?? [],

    sync_lieferando_brand: company?.sync_lieferando_brand ?? false,
    lieferando_url: company?.lieferando_url ?? null,

    import_menu: company?.import_menu ?? false,
    menu_provider: company?.menu_provider ?? null,
    menu_provider_id: company?.menu_provider_id ?? null,

    company_info: {
      pos_system: company?.company_info.pos_system ?? null,
      lead_source: company?.company_info.lead_source ?? null,
      other_platforms: company?.company_info.other_platforms ?? [],
      monthly_sales_other_platforms: company?.company_info.monthly_sales_other_platforms ?? null,
      is_franchise: company?.company_info.is_franchise ?? false,
      has_instagram_profile: company?.company_info.has_instagram_profile ?? null,
    },
  });

  const _onSave = async () => {
    setLoading(true);
    const { success: coordinatesSuccess, coordinates } = await LocationHelpers.getCoordinates(
      LocationHelpers.compileAddress(data.address, data.zipCode, data.city, data.country),
    );
    setLoading(false);

    const sanitized = {
      ...data,
      lat: coordinatesSuccess ? coordinates.lat() : data?.lat,
      lon: coordinatesSuccess ? coordinates.lng() : data?.lon,
      sync_lieferando_brand: data.sync_lieferando_brand && Boolean(data.lieferando_url),
      import_menu: data.import_menu && Boolean(data.menu_provider) && Boolean(data.menu_provider_id),
    };

    onSave(sanitized);
    onClose();
  };

  const config = {
    data,
    onChange: update,
  };

  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === _steps.length - 1;
  const toPrevStep = () => (isFirstStep ? null : setActiveStep(_steps[activeStepIndex - 1].key));
  const toNextStep = () => (isLastStep ? _onSave() : setActiveStep(_steps[activeStepIndex + 1].key));

  function isStepValid(step) {
    if (step === Step.BasicInfo) {
      return collect([data.name, data.address, data.city, data.state, data.country, data.zip_code, data.timezone])
        .when(!isDemo, (c) => c.merge([data.restaurant_type, collect(data.cuisine_types).isNotEmpty()]))
        .every(Boolean);
    }

    return (
      isStepValid(Step.BasicInfo) &&
      Boolean(data.company_info.pos_system) &&
      Boolean(data.company_info.lead_source) &&
      Boolean(![null, undefined].includes(data.company_info.monthly_sales_other_platforms?.[0])) &&
      data.company_info.has_instagram_profile !== null
    );
  }

  return (
    <Drawer
      open
      size={window.innerWidth * 1}
      onClose={onClose}
      classNames={{
        body: 'bg-gray-50 !pb-xl',
      }}
    >
      <h2 className="w-full text-center mb-xs">{t('title')}</h2>
      {_steps.length > 1 && (
        <div className="w-full lg:w-1/2 mx-auto">
          <p className="text-gray-600 w-full text-center">
            {t('description', { activeStep: activeStepIndex + 1, totalSteps: _steps.length })}
          </p>
          <Steps
            className="my-xl"
            current={activeStepIndex}
            onChange={(index) => setActiveStep(_steps[index].key)}
            items={_steps.map((item) => ({ title: t(`steps.${item.key}`) }))}
          />
        </div>
      )}
      <div className="bg-white p-lg shadow rounded-lg space-y-lg lg:w-1/2 w-full mx-auto">
        {activeStep === Step.BasicInfo && (
          <>
            <CompanyLocation {...config} isDemo={isDemo} />
            {!isDemo && (
              <>
                <CuisineType {...config} />
                <RestaurantType {...config} />
              </>
            )}
            {(!serviceProvider?.is_master || isDemo) && <CompanyProviders {...config} />}
          </>
        )}
        {activeStep === Step.BusinessDetails && <BusinessDetails {...config} />}
        <Flex justify="space-between">
          {isFirstStep ? (
            <div />
          ) : (
            <Button size="large" onClick={toPrevStep}>
              {t('buttons.back')}
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            onClick={toNextStep}
            loading={loading}
            disabled={!isStepValid(activeStep)}
          >
            {t(`buttons.${isLastStep ? 'complete_registration' : 'next'}`)}
          </Button>
        </Flex>
      </div>
    </Drawer>
  );
}
